import React from "react";
import { useNavigate } from "react-router-dom";
import { ColumnInfo, ModelInfo } from "build-ts/metadata";
import { ColumnType } from "build-ts/enums";
import { useConfigurationContext } from "context/ConfigurationContext";
import KnowledgeGraphVisualizerContainer from "modules/mkg/containers/KnowledgeGraphVisualizer";
import ThoughtSpotEmbed from "modules/datamodel/components/ThoughtSpotEmbed";
import MemoryConfigurationPanel from "modules/memory/conatiners/MemoryConfigurationPanel";
import DependenciesModal from "modules/common/DependenciesModal";
import MetricsView from "modules/metric/components/MetricsView";
import BenchmarksView from "modules/benchmark/components/BenchmarksView";
import KGColumnView from "modules/kgcolumn/components/KGColumnView";
import OntologyMappingContainer from "modules/datamodel/containers/OntologyMappingContainer";
import ModelListContainer from "modules/datamodel/containers/ModelList";

export interface ConfigPageMainPanelProps {
  activeButton: string;
  shouldReloadKG: boolean;
  modelInfo: ModelInfo | undefined;
  setModelInfo: React.Dispatch<React.SetStateAction<ModelInfo | undefined>>;
  addedColumns: ColumnInfo[];
  removedColumns: ColumnInfo[];
  setAddedColumns: React.Dispatch<React.SetStateAction<ColumnInfo[]>>;
  setRemovedColumns: React.Dispatch<React.SetStateAction<ColumnInfo[]>>;
  fetchModelList: () => Promise<void>;
  handleReloadKnowledgeGraph: () => void;
  tsMatchTsId?: string;
}

const ConfigPageMainPanel: React.FC<ConfigPageMainPanelProps> = ({
  activeButton,
  shouldReloadKG,
  modelInfo,
  setModelInfo,
  addedColumns,
  removedColumns,
  setAddedColumns,
  setRemovedColumns,
  fetchModelList,
  handleReloadKnowledgeGraph,
  tsMatchTsId,
}) => {
  const navigate = useNavigate();
  const { showDependenciesModal, dependencies, setShowDependenciesModal, setDependencies, modelList } =
    useConfigurationContext();

  const handleCloseDependenciesModal = () => {
    setShowDependenciesModal(false);
    setDependencies([]);
  };

  const renderMainPanelContent = () => {
    switch (activeButton) {
      case "ViewMKG":
        return <KnowledgeGraphVisualizerContainer shouldReload={shouldReloadKG} />;

      case "metrics":
        return <MetricsView onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />;

      case "benchmarks":
        return <BenchmarksView onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />;

      case "measures":
        return <KGColumnView columnType={ColumnType.MEASURE} onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />;

      case "attributes":
        return <KGColumnView columnType={ColumnType.ATTRIBUTE} onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />;

      case "time":
        return <KGColumnView columnType={ColumnType.TEMPORAL} onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />;

      case "createModel":
        return (
          <OntologyMappingContainer
            modelInfo={modelInfo}
            updateFlag={false}
            onBack={() => navigate("/mkg/models")}
            onReloadModelList={fetchModelList}
          />
        );

      case "updateModel":
        return (
          <OntologyMappingContainer
            updateFlag={true}
            onBack={() => navigate("/mkg/models")}
            onReloadModelList={fetchModelList}
            addedColumns={addedColumns}
            removedColumns={removedColumns}
          />
        );

      case "modelList":
        return <ModelListContainer />;

      case "thoughtspotEmbed":
        return (
          <ThoughtSpotEmbed
            setModelInfo={setModelInfo}
            modelList={modelList}
            tsModelId={tsMatchTsId}
            setAddedColumns={setAddedColumns}
            setRemovedColumns={setRemovedColumns}
          />
        );

      case "memory":
        return <MemoryConfigurationPanel />;

      default:
        return null;
    }
  };

  return (
    <>
      {renderMainPanelContent()}
      <DependenciesModal
        isOpen={showDependenciesModal}
        onClose={handleCloseDependenciesModal}
        dependencies={dependencies}
      />
    </>
  );
};

export default ConfigPageMainPanel;
